// App.jsx
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import RouteMetadata from './components/RouteMetadata';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { trackPageView } from './utils/analytics';
import { CartProvider } from './context/CartContext';

// Eager load components that are needed immediately
import CatalogPage from './pages/CatalogPage';
import PromoModal from './components/PromoModal';

// Lazy load product page
const StudioPage = lazy(() => import('./pages/StudioPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));

// Lazy load search page
const SearchPage = lazy(() => import('./pages/SearchPage'));

// Lazy load policy pages (code splitting)
const ShippingInfo = lazy(() => import('./pages/ShippingInfo'));
const ReturnPolicy = lazy(() => import('./pages/ReturnPolicy'));
const CustomOrders = lazy(() => import('./pages/CustomOrders'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

// Lazy load admin components (code splitting)
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Dashboard = lazy(() => import('./pages/DashboardPage'));

// Lazy load payment pages (code splitting)
const PayFastSuccess = lazy(() => import('./pages/PayFastSuccess'));
const PayFastCancel = lazy(() => import('./pages/PayFastCancel'));
const EventCollectionPage = lazy(() => import('./pages/EventCollectionPage'));
const EventProductPersonalisationPage = lazy(() => import('./pages/EventProductPersonalisationPage'));
const EventCollectionCartPage = lazy(() => import('./pages/EventCollectionCartPage'));
const EventOrderConfirmationPage = lazy(() => import('./pages/EventOrderConfirmationPage'));

// Component to track page views
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view whenever route changes
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoggedIn(false);
        setLoading(false);
        return;
      }
      try {
        const token = await user.getIdTokenResult();
        setLoggedIn(token.claims.admin === true);
      } catch {
        setLoggedIn(false);
      } finally {
        setLoading(false);
      }
    });

    // Firebase Analytics is automatically initialized in firebase.js

    // Cleanup function
    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div className="loading-app">Loading...</div>;
  }

  return (
    <CartProvider>
      <Router>
        <RouteMetadata /><PageViewTracker />
        <Suspense fallback={
          <div className="loading-app" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '1.2rem',
            color: 'var(--studio-accent)'
          }}>
            Loading...
          </div>
        }>
          {/* Promotional Modal - shows on all public pages */}
          <PromoModal />

          <Routes>
            {/* Public routes */}
            <Route path="/" element={<CatalogPage />} />
            <Route path="/about" element={<StudioPage />} />
            <Route path="/corporate" element={<StudioPage />} />
            <Route path="/our-work" element={<StudioPage />} />
            <Route path="/occasions" element={<StudioPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/product/:productId" element={<ProductPage />} />
            <Route path="/shipping" element={<ShippingInfo />} />
            <Route path="/returns" element={<ReturnPolicy />} />
            <Route path="/custom-orders" element={<CustomOrders />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/collections/:collectionSlug" element={<EventCollectionPage />} />
            <Route path="/collections/:collectionSlug/product/:productId" element={<EventProductPersonalisationPage />} />
            <Route path="/collections/:collectionSlug/cart" element={<EventCollectionCartPage />} />
            <Route path="/order/:orderNumber/confirmation" element={<EventOrderConfirmationPage />} />
            <Route path="/women-on-doek-2026" element={<Navigate to="/collections/women-on-doek-2026" replace />} />

            {/* Payment callback routes */}
            <Route path="/payment/success" element={<PayFastSuccess />} />
            <Route path="/payment/cancel" element={<PayFastCancel />} />

            {/* Admin routes without footer */}
            <Route path="/login" element={<LoginPage onLogin={() => setLoggedIn(true)} />} />
            <Route path="/dashboard" element={loggedIn ? <Dashboard /> : <Navigate to="/login" />} />
          </Routes>
        </Suspense>
      </Router>
    </CartProvider>
  );
}

export default App;
